import { APIStatus } from '@application/enums/api-status';
import { createSlice } from '@reduxjs/toolkit';
import { createRoleAsync, deleteRoleAsync, getAllRolesAsync, getCompanyRolesAsync, getRoleByIdAsync, getRolePermissionsAsync, toggleRoleActiveAsync, updateRoleAsync, updateRolePermissionsAsync } from './rolesThunk';
const initialState = {
    status: APIStatus.IDLE,
    roles: null,
    selectedRole: null,
    companyRoles: [],
    rolePermissions: null,
    searchString: null
};
export const RoleSlice = createSlice({
    name: 'role',
    initialState,
    reducers: {
        resetRoleApiState(state) {
            state.status = APIStatus.IDLE;
        },
        clearSelectedRole(state) {
            state.selectedRole = null;
        },
        clearCompanyRoles(state) {
            state.companyRoles = [];
        },
        clearRolePermissions(state) {
            state.rolePermissions = null;
        },
        resetSearchString(state) {
            state.searchString = null;
        },
        setSearchString(state, action) {
            state.searchString = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            // Get All Roles
            .addCase(getAllRolesAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(getAllRolesAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            state.roles = action.payload;
        })
            .addCase(getAllRolesAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            // Only set apiError if not a cancellation
            if (action.payload) {
                state.apiError = action.payload;
            }
        })
            // Create Role
            .addCase(createRoleAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(createRoleAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            if (state.roles && Array.isArray(state.roles.items) && action.payload.data) {
                state.roles.items.push(action.payload.data);
            }
        })
            .addCase(createRoleAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        })
            // Get Role By Id
            .addCase(getRoleByIdAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(getRoleByIdAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            state.selectedRole = action.payload;
        })
            .addCase(getRoleByIdAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        })
            // Update Role
            .addCase(updateRoleAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(updateRoleAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            // Ensure roles is not null and has items array
            if (state.roles && Array.isArray(state.roles.items) && action.payload.data) {
                const data = action.payload.data;
                const index = state.roles.items.findIndex((role) => role.id === data.id);
                if (index !== -1) {
                    state.roles.items[index] = data;
                }
                state.selectedRole = data;
            }
            else {
                if (action.payload.data) {
                    state.selectedRole = action.payload.data;
                }
            }
        })
            .addCase(updateRoleAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        })
            // Delete Role
            .addCase(deleteRoleAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(deleteRoleAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            // Ensure roles is not null and has items array
            if (state.roles && Array.isArray(state.roles.items) && action.payload.data) {
                const data = action.payload.data;
                state.roles.items = state.roles.items.filter((role) => role.id !== data.id);
            }
        })
            .addCase(deleteRoleAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        })
            // Get Roles By Company Id
            .addCase(getCompanyRolesAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(getCompanyRolesAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            state.companyRoles = action.payload;
        })
            .addCase(getCompanyRolesAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        })
            .addCase(toggleRoleActiveAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(toggleRoleActiveAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            // Update the active status in the roles list
            if (state.roles && Array.isArray(state.roles.items)) {
                const index = state.roles.items.findIndex((role) => role.id === action.payload.data?.id);
                if (index !== -1) {
                    state.roles.items[index].isActive = action.payload?.data?.isActive ?? false;
                }
            }
            // Optionally update selectedRole if it matches
            if (state.selectedRole && state.selectedRole?.id === action.payload.data?.id) {
                state.selectedRole.isActive = action.payload?.data?.isActive ?? false;
            }
        })
            .addCase(toggleRoleActiveAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        })
            .addCase(getRolePermissionsAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(getRolePermissionsAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
            state.rolePermissions = action.payload;
        })
            .addCase(getRolePermissionsAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = action.payload;
        })
            .addCase(updateRolePermissionsAsync.pending, (state) => {
            state.status = APIStatus.PENDING;
            state.apiError = undefined;
        })
            .addCase(updateRolePermissionsAsync.fulfilled, (state, action) => {
            state.status = APIStatus.SUCCESS;
        })
            .addCase(updateRolePermissionsAsync.rejected, (state, action) => {
            state.status = APIStatus.FAILED;
            state.apiError = undefined;
        });
    }
});
export const { resetRoleApiState, clearSelectedRole, clearCompanyRoles, clearRolePermissions, resetSearchString, setSearchString } = RoleSlice.actions;
export default RoleSlice.reducer;
